import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { clsx } from "clsx";
import { concat, findIndex, flatten, flattenDeep, groupBy, uniqBy } from "lodash-es";
import { Component, type FC } from "react";
import { connect } from "react-redux";
import { Form, getFormValues, type InjectedFormProps, reduxForm } from "redux-form";
import { ActionButton, Callout, CalloutIntent, HStack } from "widgets";
import { isFilenameOfType } from "widgets/utils";

import { FilePickerControlledDialog } from "@/modules/files/components/FilePickerControlledDialog.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

import { getFormError, getFormWarning, handleTabInForm } from "../../../app-utils/form.js";
import { type ICombinedRegistrationNgsPlaybook } from "../../../app-utils/playbook.js";
import { getUniqueAttributeId } from "../../../app-utils/sample.js";
import { extractNumberFromString, pluralizeWord } from "../../../app-utils/string.js";
import {
  type Attribute,
  type FileType,
  type Organism,
  type ProjectFile,
  type Workflow,
  type WorkflowParam,
} from "../../../graphql.js";
import {
  getGroupedNearPathsBeforeLastUnderscore,
  getParentPath,
  isBamOrSamFile,
  isFastQFile,
} from "../../../modules/files/utils/file.js";
import {
  fetchOrganismsFromSampleNgsFiles,
  getGenomeWorkflowParams,
  getOrderedListOfTrackerAndWorkflowAttributes,
  type IPlaybookExecutionState,
  isNgsDataFileAttributeByUsage,
  isNgsDataFileWorkflowParam,
  updatePlaybookExecutionState,
} from "../../../redux/modules/playbook-execution.js";
import { type IAppState } from "../../../redux/reducer.js";
import { ATTRIBUTE_FILE_TYPES, PLAYBOOK_TYPE, WORKFLOW_PARAM_USAGE } from "../../../settings/settings.js";
import calloutStyle from "../../../styles/callout.module.scss";
import FormSection from "../../core/form/layout/FormSection.js";
import AttributeInputField, { getSelectedOrganismFromForm } from "../../core/forms/AttributeInputField.js";
import { SortableItem, SortableList } from "../../core/forms/Sortable.js";
import style from "../../core/forms/styles/Sortable.module.scss";
import { FieldTitle } from "../FieldTitle.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

type ICombinedRegistrationNgsAnalysisDataScreenOwnProps = IPlaybookWizardStepProvidedProps;

interface ICombinedRegistrationNgsAnalysisDataScreenAppStateProps {
  playbookExecution: IPlaybookExecutionState;
  globalFormError: string;
  globalFormWarning: string;
  requireUnalignedReadsInput: boolean;
  formValues: any;
}

interface ICombinedRegistrationNgsAnalysisDataScreenDispatchProps {
  updatePlaybookExecutionState: (state: Partial<IPlaybookExecutionState>) => void;
  fetchOrganismsFromSampleNgsFiles: () => Promise<Organism[]>;
}

export interface ICombinedRegistrationNgsAnalysisDataScreenProps
  extends ICombinedRegistrationNgsAnalysisDataScreenAppStateProps,
    ICombinedRegistrationNgsAnalysisDataScreenDispatchProps,
    ICombinedRegistrationNgsAnalysisDataScreenOwnProps,
    InjectedFormProps<any, any>,
    IPlaybookWizardStepInjectedProps {
  // only if this screen is a redux-form
}

function mapStateToProps(
  state: IAppState,
  _ownProps: ICombinedRegistrationNgsAnalysisDataScreenProps,
): ICombinedRegistrationNgsAnalysisDataScreenAppStateProps {
  const ngsFilesWorkflowParam = state.playbookExecution.workflow.workflowParams.find(isNgsDataFileWorkflowParam);

  return {
    playbookExecution: state.playbookExecution,
    globalFormError: getFormError(
      CombinedRegistrationNgsAnalysisDataScreen.COMBINED_REGISTRATION_NGS_RAW_DATA_SCREEN,
      state,
    ),
    globalFormWarning: getFormWarning(
      CombinedRegistrationNgsAnalysisDataScreen.COMBINED_REGISTRATION_NGS_RAW_DATA_SCREEN,
      state,
    ),
    requireUnalignedReadsInput: ngsFilesWorkflowParam.usage === WORKFLOW_PARAM_USAGE.UNALIGNED_READS_INPUT_FILE_PATHS,
    formValues: getFormValues(CombinedRegistrationNgsAnalysisDataScreen.COMBINED_REGISTRATION_NGS_RAW_DATA_SCREEN)(
      state,
    ),
  };
}

function mapDispatchToProps(dispatch): ICombinedRegistrationNgsAnalysisDataScreenDispatchProps {
  return {
    updatePlaybookExecutionState: (state: Partial<IPlaybookExecutionState>) =>
      dispatch(updatePlaybookExecutionState(state)),
    fetchOrganismsFromSampleNgsFiles: () => dispatch(fetchOrganismsFromSampleNgsFiles()),
  };
}

interface ICombinedRegistrationNgsAnalysisDataScreenState {
  isFilePickerOpen: boolean;
  ngsRawDataFiles: ProjectFile[][]; // only applicable for ngs analysis playbook (in other cases the data files are attached to samples)
  error: string;
}

class CombinedRegistrationNgsAnalysisDataScreen extends Component<
  ICombinedRegistrationNgsAnalysisDataScreenProps,
  ICombinedRegistrationNgsAnalysisDataScreenState
> {
  public static COMBINED_REGISTRATION_NGS_RAW_DATA_SCREEN = "COMBINED_REGISTRATION_NGS_RAW_DATA_SCREEN";

  constructor(props: ICombinedRegistrationNgsAnalysisDataScreenProps) {
    super(props);
    this.state = {
      isFilePickerOpen: false,
      ngsRawDataFiles: props.playbookExecution.ngsRawDataFiles,
      error: null,
    };
  }

  componentDidMount() {
    if (this.props.playbookExecution.playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES) {
      // infer a genome from the files selection from sample file attributes
      this.props.fetchOrganismsFromSampleNgsFiles().then(this.assignSuggestionOfOrganismToWorkflowParam);
    }

    this.props.initialize(this.props.playbookExecution.workflowValues);
  }

  assignSuggestionOfOrganismToWorkflowParam = (genomes: Organism[]) => {
    const suggestedGenome: Organism = genomes?.length > 0 && genomes[0];

    if (suggestedGenome) {
      const workflowGenomeAttributes: Attribute[] = getGenomeWorkflowParams(
        this.props.playbookExecution.workflowAttributes,
      );
      workflowGenomeAttributes.forEach((a: Attribute) => {
        const uniqueIdentifier: string = getUniqueAttributeId(a);
        const currValue: Organism | string[] = this.props.formValues[uniqueIdentifier];
        if (!currValue || (Array.isArray(currValue) && currValue.length === 0)) {
          this.props.change(uniqueIdentifier, suggestedGenome);
        }
      });
    }
  };

  componentDidUpdate(
    _prevProps: ICombinedRegistrationNgsAnalysisDataScreenProps,
    prevState: ICombinedRegistrationNgsAnalysisDataScreenState,
  ) {
    // Set the genome input fields to a value which reflects the genomes associated with the selected bam file(s), if possible
    if (prevState.ngsRawDataFiles !== this.state.ngsRawDataFiles) {
      if (this.state.ngsRawDataFiles?.length > 0) {
        // infer a genome from the list of ngs raw data files
        const firstGenomes: Organism[] = flattenDeep<ProjectFile>(this.state.ngsRawDataFiles)
          .filter((f) => f.organisms && f.organisms.length > 0)
          .map((f) => f.organisms[0]);
        this.assignSuggestionOfOrganismToWorkflowParam(firstGenomes);
      }
    }
  }

  static validateAnnotationField = (
    value: any,
    allValues: any,
    props: ICombinedRegistrationNgsAnalysisDataScreenProps,
  ) => {
    if (!getSelectedOrganismFromForm(props.playbookExecution.workflowAttributes, allValues) && !value?.length) {
      return `Your special input is required`;
    }
    return undefined;
  };

  validateRawDataFiles = () => {
    if (!this.state.ngsRawDataFiles.length) {
      return this.setState({ error: "No NGS data files selected" });
    }
    const allSameNumberOfFiles: boolean = this.state.ngsRawDataFiles.every(
      (mapping) => mapping.length === this.state.ngsRawDataFiles[0].length,
    );
    if (!allSameNumberOfFiles) {
      return this.setState({
        error: "Every sample must either have one raw file or two (for paired-end data). Mixing is not supported.",
      });
    }
    this.setState({ error: null });
    return true;
  };

  handleSubmitData = (values: Record<string, any>) => {
    if (this.props.playbookExecution.playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS) {
      if (!this.validateRawDataFiles()) {
        return;
      }
    }

    this.props.updatePlaybookExecutionState({
      workflowValues: { ...this.props.playbookExecution.workflowValues, ...values },
      ngsRawDataFiles: this.state.ngsRawDataFiles,
    });

    this.props.proceedWithNextStep();
  };

  getDataFileLabel = () => {
    return this.props.requireUnalignedReadsInput ? "NGS raw data" : "Aligned NGS data (BAM)";
  };

  render() {
    const filesSelectedCount = flatten(this.state.ngsRawDataFiles).length;

    const selectedGenome: Organism = getSelectedOrganismFromForm(
      this.props.playbookExecution.workflowAttributes,
      this.props.formValues,
    );

    return (
      <PlaybookWizardStep
        {...this.props}
        playbookPage={{
          ...this.props.playbookPage,
          label: this.props.playbookPage.label || this.getDataFileLabel(),
          description:
            this.props.playbookExecution.playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS
              ? `Select the ${this.getDataFileLabel()} files for your analysis.`
              : `Select the ${this.getDataFileLabel()} files corresponding to the samples you are about to register`,
        }}
        wizardFooterData={{
          error: this.props.globalFormError || this.props.error || this.state.error,
          warning: this.props.globalFormWarning,
          nextButtonProps: {
            label: "Next",
            isLoading: !!this.props.asyncValidating,
            onPress: this.props.handleSubmit(this.handleSubmitData),
          },
        }}
      >
        <Form
          className="form-horizontal"
          onSubmit={this.props.handleSubmit(this.handleSubmitData)}
          onKeyDownCapture={handleTabInForm}
        >
          {this.props.playbookExecution.playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS && ( // in all other cases the raw data is attached to sample attributes
            <FormSection>
              <FormInputGroup horizontalLayout>
                <FormLabel>
                  <FieldTitle label={this.getDataFileLabel()} mandatory />
                </FormLabel>
                <FormInputWidget>
                  <HStack>
                    <ActionButton
                      label={"Select Files"}
                      onPress={() => this.setState({ isFilePickerOpen: true })}
                      icon={"folder_open"}
                    />
                    <span>
                      {filesSelectedCount ? filesSelectedCount : "No"}&nbsp;
                      {pluralizeWord("file", filesSelectedCount)} selected
                    </span>
                  </HStack>
                  {this.props.requireUnalignedReadsInput && (
                    <Callout intent={CalloutIntent.Info} className={clsx(calloutStyle.formMargin)}>
                      For paired-end NGS Data, be sure to include all applicable files. For example, a single sample may
                      be represented by two separate files with names ending in _1 and _2.
                    </Callout>
                  )}
                  {!!this.state.ngsRawDataFiles.length && (
                    <SortableList id="dataseries-list" onDragEnd={this.reorderMapping}>
                      <SortableContext items={this.state.ngsRawDataFiles.map((files) => files[0].id)}>
                        {this.state.ngsRawDataFiles.map((files: ProjectFile[], index: number) => (
                          <SortableItem
                            key={`mapping-${index}`}
                            id={files[0].id}
                            index={index}
                            onRemove={() => this.removeMapping(index)}
                          >
                            <span className={style.label}>
                              {files.map((f) => f.fileName).join(", ")}
                              {files.length === 1 ? ( // in case of sample mapping, show the path only if there is only one single file, otherwise this is too much info for this component
                                <div className={style.sub_label}>
                                  {files.map((f) => getParentPath(f.humanReadablePath)).join(", ")}
                                </div>
                              ) : null}
                            </span>
                          </SortableItem>
                        ))}
                      </SortableContext>
                    </SortableList>
                  )}
                </FormInputWidget>

                <FilePickerControlledDialog
                  isSelectableFilter={(file) => {
                    const filename = file.fileName ?? file.fullPath;
                    return this.props.requireUnalignedReadsInput
                      ? isFastQFile({ ...file, fileName: filename })
                      : isBamOrSamFile({ ...file, fileName: filename });
                  }}
                  baseFilterHint={`Files of type ${
                    this.props.requireUnalignedReadsInput ? "FASTQ" : "BAM, SAM"
                  } can be selected.`}
                  fileDefinitionTypes={this.props.requireUnalignedReadsInput ? ["FASTQ"] : ["BAM", "SAM"]}
                  onValid={this.addFiles}
                  isOpen={this.state.isFilePickerOpen}
                  onClose={() => this.setState({ isFilePickerOpen: false })}
                />
              </FormInputGroup>
            </FormSection>
          )}

          <ClassicExperimentLevelInputFields
            selectedWorkflow={this.props.playbookExecution.workflow}
            workflowAttributes={this.props.playbookExecution.workflowAttributes}
            playbook={this.props.playbookExecution.playbook}
            selectedGenome={selectedGenome}
            acceptExplorerSequences={true}
          />
        </Form>
      </PlaybookWizardStep>
    );
  }

  removeMapping = (indexOfMappingToRemove: number) => {
    this.setState(
      {
        ngsRawDataFiles: this.state.ngsRawDataFiles.filter((_s, index: number) => index !== indexOfMappingToRemove),
      },
      this.validateRawDataFiles,
    );
  };

  reorderMapping = ({ active, over }) => {
    const fromIndex = findIndex(this.state.ngsRawDataFiles, (item) => item[0].id === active.id);
    const toIndex = findIndex(this.state.ngsRawDataFiles, (item) => item[0].id === over.id);

    if (fromIndex !== toIndex) {
      this.setState({ ngsRawDataFiles: arrayMove(this.state.ngsRawDataFiles, fromIndex, toIndex) });
    }
  };

  addFiles = (files: ProjectFile[]) => {
    if (files.length) {
      // Flatten the files groups to search for duplicates and updates groups with the new additions.
      // We can not have a duplicate file, so it's ok to recreate the groups from scratch.
      const ngsRawDataFiles = getGroupedNearPathsBeforeLastUnderscore(
        uniqBy(concat(flattenDeep(this.state.ngsRawDataFiles), files), (file: ProjectFile) => file.fullPath),
      );

      this.setState(
        {
          ngsRawDataFiles: ngsRawDataFiles,
        },
        this.validateRawDataFiles,
      );
    }
    this.setState({ isFilePickerOpen: false });
    return Promise.resolve();
  };
}

export default connect<
  ICombinedRegistrationNgsAnalysisDataScreenAppStateProps,
  ICombinedRegistrationNgsAnalysisDataScreenDispatchProps,
  ICombinedRegistrationNgsAnalysisDataScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<any, ICombinedRegistrationNgsAnalysisDataScreenOwnProps>({
    form: CombinedRegistrationNgsAnalysisDataScreen.COMBINED_REGISTRATION_NGS_RAW_DATA_SCREEN,
  })(CombinedRegistrationNgsAnalysisDataScreen),
);

interface IClassicExperimentLevelInputFieldsProps {
  selectedWorkflow: Workflow;
  workflowAttributes: Attribute[];
  playbook: ICombinedRegistrationNgsPlaybook;
  selectedGenome: Organism;
  onlyExplorerGenome?: boolean;
  acceptExplorerSequences?: boolean;
}

const ClassicExperimentLevelInputFields: FC<IClassicExperimentLevelInputFieldsProps> = (props) => {
  const originalWorkflowParams = props.selectedWorkflow.workflowParams;

  const workflowParams: Attribute[] = props.workflowAttributes.filter(
    (attribute: Attribute) => !isNgsDataFileAttributeByUsage(attribute),
  );

  // the selection  does not contain all the required data (e.g. stored only the identifiers or so) therefore we need to flesh out this selection with the available data

  const orderedListOfConfiguredWorkflowParams: Attribute[] = getOrderedListOfTrackerAndWorkflowAttributes(
    props.playbook.config.experimentInput,
    workflowParams,
  )
    // sort by activityIndex (natural ordering of the workflow) ; should be taken care of by configuration step, but we do it again here for existing configurations
    .sort((workflowParam1, workflowParam2) => {
      const originalWorkflowParam1: WorkflowParam = originalWorkflowParams.find((wp) => wp.id === workflowParam1.id);
      const originalWorkflowParam2: WorkflowParam = originalWorkflowParams.find((wp) => wp.id === workflowParam2.id);
      return extractNumberFromString(originalWorkflowParam1.id) - extractNumberFromString(originalWorkflowParam2.id);
    });

  const groupedWorkflowParams = groupBy(orderedListOfConfiguredWorkflowParams, (workflowParam: Attribute) => {
    // group by activity index (same activity can happen multiple times in one single workflow)
    const originalWorkflowParam: WorkflowParam = originalWorkflowParams.find((wp) => wp.id === workflowParam.id);
    return originalWorkflowParam.activityIndex;
  });

  // Building the message if selection of an annotation file is not required.
  const selectedGenome: Organism = props.selectedGenome;
  const selectedGenomeLabel: string = selectedGenome ? selectedGenome.name : "";

  return (
    <>
      {groupedWorkflowParams &&
        Object.keys(groupedWorkflowParams).map((activityIndex: string, index) => {
          const activityName: string = groupedWorkflowParams[activityIndex][0].groupName;
          return (
            <FormSection title={activityName} key={`attribute-group-${index}`}>
              {groupedWorkflowParams[activityIndex].map((attribute: Attribute) => (
                <AttributeInputField
                  attribute={{
                    ...attribute,
                    groupName: null,
                  }} // we remove the groupName because it is already mentioned in the input section title
                  key={`attribute-${attribute.id}`}
                  noFileSelectionText={
                    attribute.usage === WORKFLOW_PARAM_USAGE.GENOMIC_ANNOTATION_INPUT && selectedGenomeLabel
                      ? `No files selected. Annotations for ${selectedGenomeLabel} will be taken from the database.`
                      : null
                  }
                  validate={
                    attribute.usage === WORKFLOW_PARAM_USAGE.GENOMIC_ANNOTATION_INPUT
                      ? CombinedRegistrationNgsAnalysisDataScreen.validateAnnotationField
                      : null
                  }
                  selectedGenome={selectedGenome}
                  onlyExplorerGenome={props.onlyExplorerGenome}
                  acceptExplorerSequences={props.acceptExplorerSequences}
                />
              ))}
            </FormSection>
          );
        })}
    </>
  );
};

export { ClassicExperimentLevelInputFields };
